import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Calendar, ClipboardList, ChevronRight, Search, Users,
  CheckCircle2, XCircle, Clock, AlertCircle, CalendarOff,
  TrendingUp, Building2, Plus, ChevronLeft, Filter
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  APPROVED:  { label: "Approved",  bg: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: CheckCircle2, dot: "bg-emerald-500" },
  SUBMITTED: { label: "Pending",   bg: "bg-amber-100 text-amber-700 border-amber-200",      icon: Clock,         dot: "bg-amber-500" },
  REJECTED:  { label: "Rejected",  bg: "bg-red-100 text-red-700 border-red-200",             icon: XCircle,       dot: "bg-red-500" },
  CANCELLED: { label: "Cancelled", bg: "bg-slate-100 text-slate-600 border-slate-200",      icon: AlertCircle,   dot: "bg-slate-400" },
  WITHDRAWN: { label: "Withdrawn", bg: "bg-slate-100 text-slate-600 border-slate-200",      icon: AlertCircle,   dot: "bg-slate-400" },
};

const ATT_STATUS_CONFIG = {
  PRESENT:   { label: "Present",   bg: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  ABSENT:    { label: "Absent",    bg: "bg-red-100 text-red-700",         dot: "bg-red-500" },
  LATE:      { label: "Late",      bg: "bg-amber-100 text-amber-700",     dot: "bg-amber-500" },
  WFH:       { label: "WFH",       bg: "bg-blue-100 text-blue-700",       dot: "bg-blue-500" },
  HOLIDAY:   { label: "Holiday",   bg: "bg-purple-100 text-purple-700",   dot: "bg-purple-500" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || { label: status, bg: "bg-slate-100 text-slate-600 border-slate-200", dot: "bg-slate-400" };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function AttStatusBadge({ status, isHoliday, isWfh }) {
  const key = isHoliday ? "HOLIDAY" : isWfh ? "WFH" : status;
  const cfg = ATT_STATUS_CONFIG[key] || { label: status, bg: "bg-slate-100 text-slate-600", dot: "bg-slate-400" };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cfg.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

export default function AttendanceLeave() {
  const navigate = useNavigate();
  const token = localStorage.getItem("academiaos_token");
  const headers = { Authorization: `Bearer ${token}` };

  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [activeTab, setActiveTab] = useState("leave"); // "leave" | "attendance"
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  // Leave state
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveSummary, setLeaveSummary] = useState(null);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestDetail, setRequestDetail] = useState(null);
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [filterStatus, setFilterStatus] = useState("");

  // Attendance state
  const [attSummary, setAttSummary] = useState(null);
  const [attCalendar, setAttCalendar] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [teamAttendance, setTeamAttendance] = useState([]);
  const [attLoading, setAttLoading] = useState(false);

  // Submit form state
  const [submitForm, setSubmitForm] = useState({ leave_type_id: "", from_date: "", to_date: "", reason: "" });
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  // Approval
  const [approvalError, setApprovalError] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  useEffect(() => {
    fetchWorkers();
    fetchLeaveTypes();
    fetchLeaveSummary();
    fetchTeamAttendance();
  }, []);

  useEffect(() => {
    if (selectedWorker) {
      if (activeTab === "leave") {
        fetchLeaveData(selectedWorker.worker_id);
      } else {
        fetchAttendanceData(selectedWorker.worker_id);
      }
    }
  }, [selectedWorker, activeTab]);

  useEffect(() => {
    if (selectedWorker) {
      fetchLeaveRequests(selectedWorker.worker_id);
    }
  }, [filterStatus, selectedWorker]);

  const fetchWorkers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/v1/peo01/workers`, { headers, params: { limit: 50 } });
      const list = res.data.workers || [];
      setWorkers(list);
      if (list.length > 0) setSelectedWorker(list[0]);
    } catch (e) {
      console.error("Workers fetch failed", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaveTypes = async () => {
    try {
      const res = await axios.get(`${API}/api/v1/peo02/leave/types`, { headers });
      setLeaveTypes(res.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchLeaveSummary = async () => {
    try {
      const res = await axios.get(`${API}/api/v1/peo02/leave/summary`, { headers });
      setLeaveSummary(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchTeamAttendance = async () => {
    try {
      const res = await axios.get(`${API}/api/v1/peo02/attendance/team-summary`, { headers });
      setTeamAttendance(res.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchLeaveData = async (workerId) => {
    setLeaveLoading(true);
    try {
      const [reqRes, balRes] = await Promise.all([
        axios.get(`${API}/api/v1/peo02/leave/requests`, { headers, params: { worker_id: workerId, limit: 20, ...(filterStatus && { status: filterStatus }) } }),
        axios.get(`${API}/api/v1/peo02/leave/balance/${workerId}`, { headers }),
      ]);
      setLeaveRequests(reqRes.data || []);
      setLeaveBalance(balRes.data || []);
    } catch (e) {
      console.error("Leave fetch failed", e);
    } finally {
      setLeaveLoading(false);
    }
  };

  const fetchLeaveRequests = async (workerId) => {
    if (!workerId) return;
    try {
      const params = { worker_id: workerId, limit: 20 };
      if (filterStatus) params.status = filterStatus;
      const res = await axios.get(`${API}/api/v1/peo02/leave/requests`, { headers, params });
      setLeaveRequests(res.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchAttendanceData = async (workerId) => {
    setAttLoading(true);
    try {
      const [sumRes, calRes, schedRes] = await Promise.all([
        axios.get(`${API}/api/v1/peo02/attendance/summary/${workerId}`, { headers }),
        axios.get(`${API}/api/v1/peo02/attendance/calendar/${workerId}`, { headers }),
        axios.get(`${API}/api/v1/peo02/attendance/schedule/${workerId}`, { headers }),
      ]);
      setAttSummary(sumRes.data);
      setAttCalendar(calRes.data);
      setSchedule(schedRes.data);
    } catch (e) {
      console.error("Attendance fetch failed", e);
    } finally {
      setAttLoading(false);
    }
  };

  const fetchRequestDetail = async (requestId) => {
    try {
      const res = await axios.get(`${API}/api/v1/peo02/leave/requests/${requestId}`, { headers });
      setRequestDetail(res.data);
    } catch (e) { console.error(e); }
  };

  const handleApprove = async (requestId) => {
    setApprovalError("");
    try {
      await axios.post(`${API}/api/v1/peo02/leave/requests/${requestId}/approve`, { remarks: "Approved" }, { headers });
      await fetchLeaveData(selectedWorker.worker_id);
      setSelectedRequest(null);
      setRequestDetail(null);
    } catch (e) {
      setApprovalError(e.response?.data?.detail || "Approval failed");
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { setApprovalError("Rejection reason is required"); return; }
    setApprovalError("");
    try {
      await axios.post(`${API}/api/v1/peo02/leave/requests/${pendingAction}/reject`, { reason: rejectReason }, { headers });
      await fetchLeaveData(selectedWorker.worker_id);
      setSelectedRequest(null); setRequestDetail(null);
      setShowRejectModal(false); setRejectReason(""); setPendingAction(null);
    } catch (e) {
      setApprovalError(e.response?.data?.detail || "Rejection failed");
    }
  };

  const handleSubmitLeave = async (e) => {
    e.preventDefault();
    setSubmitError(""); setSubmitSuccess("");
    try {
      await axios.post(`${API}/api/v1/peo02/leave/requests`, {
        worker_id: selectedWorker.worker_id,
        ...submitForm,
      }, { headers });
      setSubmitSuccess("Leave request submitted successfully");
      setShowSubmitForm(false);
      setSubmitForm({ leave_type_id: "", from_date: "", to_date: "", reason: "" });
      await fetchLeaveData(selectedWorker.worker_id);
    } catch (e) {
      setSubmitError(e.response?.data?.detail || "Submission failed");
    }
  };

  const filteredWorkers = workers.filter(w =>
    !search || w.display_name?.toLowerCase().includes(search.toLowerCase()) ||
    w.worker_number?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="flex h-full bg-slate-950 text-slate-100">
      {/* ── Worker List Panel ─────────────────────────────────────────── */}
      <div className="w-72 border-r border-slate-800 flex flex-col">
        <div className="p-4 border-b border-slate-800">
          <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
            <Users size={14} className="text-teal-400" />
            Workforce
          </h2>
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-2.5 text-slate-500" />
            <input
              data-testid="att-leave-worker-search"
              className="w-full bg-slate-900 border border-slate-700 rounded-lg pl-8 pr-3 py-2 text-xs text-slate-300 placeholder-slate-500 focus:outline-none focus:border-teal-500"
              placeholder="Search employees…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>

        {/* Top-level stats */}
        {leaveSummary && (
          <div className="grid grid-cols-3 gap-0 border-b border-slate-800 text-center">
            {[
              { label: "Pending", val: leaveSummary.pending, color: "text-amber-400" },
              { label: "Approved", val: leaveSummary.approved, color: "text-emerald-400" },
              { label: "Rejected", val: leaveSummary.rejected, color: "text-red-400" },
            ].map(s => (
              <div key={s.label} className="py-3 px-2 border-r border-slate-800 last:border-r-0">
                <div className={`text-xl font-bold ${s.color}`}>{s.val}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-xs text-slate-500">Loading…</div>
          ) : filteredWorkers.map(w => (
            <button
              key={w.worker_id}
              data-testid={`worker-item-${w.worker_id}`}
              onClick={() => { setSelectedWorker(w); setSelectedRequest(null); setRequestDetail(null); }}
              className={`w-full text-left px-4 py-3 border-b border-slate-800/60 hover:bg-slate-800/40 transition-colors ${selectedWorker?.worker_id === w.worker_id ? "bg-slate-800 border-l-2 border-l-teal-500" : ""}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-teal-700 to-teal-900 flex items-center justify-center text-xs font-bold text-teal-200 flex-shrink-0">
                  {(w.display_name || "?")[0]}
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-medium text-slate-200 truncate">{w.display_name}</div>
                  <div className="text-[10px] text-slate-500 truncate">{w.worker_number} · {w.department_name || "—"}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* ── Main Content ──────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {selectedWorker ? (
          <>
            {/* Header */}
            <div className="border-b border-slate-800 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-4">
                <div>
                  <h1 className="text-base font-semibold text-slate-100" data-testid="att-leave-employee-name">
                    {selectedWorker.display_name}
                  </h1>
                  <p className="text-xs text-slate-500">{selectedWorker.worker_number} · {selectedWorker.department_name}</p>
                </div>
              </div>

              {/* Tab toggle */}
              <div className="flex bg-slate-900 rounded-lg p-1 gap-1">
                {[
                  { key: "leave", label: "Leave", icon: ClipboardList },
                  { key: "attendance", label: "Attendance", icon: Calendar },
                ].map(t => (
                  <button
                    key={t.key}
                    data-testid={`tab-${t.key}`}
                    onClick={() => { setActiveTab(t.key); setSelectedRequest(null); setRequestDetail(null); }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${activeTab === t.key ? "bg-teal-600 text-white" : "text-slate-400 hover:text-slate-200"}`}
                  >
                    <t.icon size={12} />
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === "leave" ? (
                <LeavePanel
                  worker={selectedWorker}
                  leaveRequests={leaveRequests}
                  leaveBalance={leaveBalance}
                  leaveTypes={leaveTypes}
                  loading={leaveLoading}
                  selectedRequest={selectedRequest}
                  requestDetail={requestDetail}
                  showSubmitForm={showSubmitForm}
                  submitForm={submitForm}
                  submitError={submitError}
                  submitSuccess={submitSuccess}
                  filterStatus={filterStatus}
                  approvalError={approvalError}
                  showRejectModal={showRejectModal}
                  rejectReason={rejectReason}
                  onSelectRequest={id => { setSelectedRequest(id); fetchRequestDetail(id); }}
                  onApprove={handleApprove}
                  onOpenReject={(id) => { setPendingAction(id); setShowRejectModal(true); setApprovalError(""); }}
                  onReject={handleReject}
                  onSetShowRejectModal={setShowRejectModal}
                  onRejectReasonChange={setRejectReason}
                  onShowSubmitForm={() => { setShowSubmitForm(true); setSelectedRequest(null); }}
                  onHideSubmitForm={() => setShowSubmitForm(false)}
                  onSubmitFormChange={(k, v) => setSubmitForm(f => ({ ...f, [k]: v }))}
                  onSubmitLeave={handleSubmitLeave}
                  onFilterStatus={setFilterStatus}
                />
              ) : (
                <AttendancePanel
                  summary={attSummary}
                  calendar={attCalendar}
                  schedule={schedule}
                  loading={attLoading}
                  teamAttendance={teamAttendance}
                />
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center text-slate-500">
              <Users size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Select an employee to view attendance & leave</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Leave Panel ──────────────────────────────────────────────────────────────
function LeavePanel({
  worker, leaveRequests, leaveBalance, leaveTypes, loading,
  selectedRequest, requestDetail, showSubmitForm, submitForm,
  submitError, submitSuccess, filterStatus, approvalError,
  showRejectModal, rejectReason,
  onSelectRequest, onApprove, onOpenReject, onReject,
  onSetShowRejectModal, onRejectReasonChange,
  onShowSubmitForm, onHideSubmitForm, onSubmitFormChange, onSubmitLeave,
  onFilterStatus,
}) {
  return (
    <div className="grid grid-cols-5 gap-6">
      {/* Left: Request list */}
      <div className="col-span-2 space-y-4">
        {/* Balance cards */}
        <div>
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Leave Balance</h3>
          <div className="grid grid-cols-2 gap-2">
            {leaveBalance.map(b => (
              <div key={b.leave_plan_id} className="bg-slate-900 border border-slate-800 rounded-xl p-3">
                <div className="text-xs text-slate-400 mb-1">{b.leave_type_code}</div>
                <div className="flex items-end gap-1">
                  <span className="text-2xl font-bold text-teal-400">{b.available_days}</span>
                  <span className="text-xs text-slate-500 pb-1">/ {b.entitled_days}d</span>
                </div>
                <div className="text-[10px] text-slate-500 mt-1 truncate">{b.leave_type_name}</div>
                <div className="mt-2 bg-slate-800 rounded-full h-1.5">
                  <div
                    className="bg-teal-500 h-1.5 rounded-full"
                    style={{ width: `${Math.max(0, Math.min(100, (b.available_days / b.entitled_days) * 100))}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Requests list */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Leave Requests</h3>
            <div className="flex items-center gap-2">
              <select
                data-testid="leave-filter-status"
                value={filterStatus}
                onChange={e => onFilterStatus(e.target.value)}
                className="bg-slate-900 border border-slate-700 text-slate-400 text-[10px] rounded-lg px-2 py-1 focus:outline-none focus:border-teal-500"
              >
                <option value="">All</option>
                <option value="SUBMITTED">Pending</option>
                <option value="APPROVED">Approved</option>
                <option value="REJECTED">Rejected</option>
              </select>
              <button
                data-testid="btn-new-leave-request"
                onClick={onShowSubmitForm}
                className="flex items-center gap-1 bg-teal-600 hover:bg-teal-500 text-white text-[10px] font-medium px-2 py-1 rounded-lg transition-colors"
              >
                <Plus size={11} />
                New
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-6 text-xs text-slate-500">Loading…</div>
          ) : leaveRequests.length === 0 ? (
            <div className="text-center py-8 text-xs text-slate-500">No leave requests found</div>
          ) : leaveRequests.map(req => (
            <button
              key={req.id}
              data-testid={`leave-request-${req.id}`}
              onClick={() => onSelectRequest(req.id)}
              className={`w-full text-left border rounded-xl p-3 mb-2 transition-colors ${selectedRequest === req.id ? "border-teal-600 bg-slate-800/60" : "border-slate-800 bg-slate-900/50 hover:border-slate-700"}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-medium text-slate-200">{req.leave_type_name}</div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{req.from_date} — {req.to_date}</div>
                  <div className="text-[10px] text-slate-500">{req.requested_days}d · {req.request_number}</div>
                </div>
                <StatusBadge status={req.status} />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Right: Detail / Submit form */}
      <div className="col-span-3">
        {showSubmitForm ? (
          <SubmitForm
            leaveTypes={leaveTypes}
            form={submitForm}
            error={submitError}
            success={submitSuccess}
            onChange={onSubmitFormChange}
            onSubmit={onSubmitLeave}
            onCancel={onHideSubmitForm}
          />
        ) : selectedRequest && requestDetail ? (
          <RequestDetail
            detail={requestDetail}
            approvalError={approvalError}
            showRejectModal={showRejectModal}
            rejectReason={rejectReason}
            onApprove={() => onApprove(selectedRequest)}
            onOpenReject={() => onOpenReject(selectedRequest)}
            onReject={onReject}
            onSetShowRejectModal={onSetShowRejectModal}
            onRejectReasonChange={onRejectReasonChange}
          />
        ) : (
          <div className="flex items-center justify-center h-48 text-slate-600">
            <div className="text-center">
              <ClipboardList size={32} className="mx-auto mb-2 opacity-30" />
              <p className="text-xs">Select a request or click New to submit</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SubmitForm({ leaveTypes, form, error, success, onChange, onSubmit, onCancel }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
      <h3 className="text-sm font-semibold text-slate-200 mb-5">Submit Leave Request</h3>
      {error && <div data-testid="submit-leave-error" className="mb-4 p-3 bg-red-900/40 border border-red-700 rounded-lg text-xs text-red-300">{error}</div>}
      {success && <div data-testid="submit-leave-success" className="mb-4 p-3 bg-emerald-900/40 border border-emerald-700 rounded-lg text-xs text-emerald-300">{success}</div>}
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="text-xs text-slate-400 mb-1.5 block">Leave Type</label>
          <select
            data-testid="leave-type-select"
            required
            value={form.leave_type_id}
            onChange={e => onChange("leave_type_id", e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-teal-500"
          >
            <option value="">Select leave type…</option>
            {leaveTypes.map(lt => (
              <option key={lt.id} value={lt.id}>{lt.name} ({lt.type_code})</option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">From Date</label>
            <input
              type="date" required data-testid="leave-from-date"
              value={form.from_date} onChange={e => onChange("from_date", e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-teal-500"
            />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1.5 block">To Date</label>
            <input
              type="date" required data-testid="leave-to-date"
              value={form.to_date} onChange={e => onChange("to_date", e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-teal-500"
            />
          </div>
        </div>
        <div>
          <label className="text-xs text-slate-400 mb-1.5 block">Reason</label>
          <textarea
            required data-testid="leave-reason"
            rows={3} value={form.reason} onChange={e => onChange("reason", e.target.value)}
            placeholder="Reason for leave request…"
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-teal-500 resize-none"
          />
        </div>
        <div className="flex gap-2 pt-1">
          <button type="submit" data-testid="btn-submit-leave" className="flex-1 bg-teal-600 hover:bg-teal-500 text-white text-sm font-medium py-2 rounded-lg transition-colors">
            Submit Request
          </button>
          <button type="button" onClick={onCancel} className="flex-1 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm py-2 rounded-lg transition-colors">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function RequestDetail({ detail, approvalError, showRejectModal, rejectReason, onApprove, onOpenReject, onReject, onSetShowRejectModal, onRejectReasonChange }) {
  const isPending = detail.status === "SUBMITTED";
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-slate-200">{detail.leave_type_name}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{detail.request_number}</p>
        </div>
        <StatusBadge status={detail.status} />
      </div>

      <div className="grid grid-cols-2 gap-3 mb-5">
        {[
          { label: "Period", val: `${detail.from_date} — ${detail.to_date}` },
          { label: "Days", val: `${detail.requested_days} day${detail.requested_days !== 1 ? "s" : ""}` },
          { label: "Pay Impact", val: detail.pay_impact_classification || "—" },
          { label: "Submitted", val: detail.submitted_at ? new Date(detail.submitted_at).toLocaleDateString() : "—" },
        ].map(r => (
          <div key={r.label} className="bg-slate-800/60 rounded-xl p-3">
            <div className="text-[10px] text-slate-500 mb-0.5">{r.label}</div>
            <div className="text-xs font-medium text-slate-200">{r.val}</div>
          </div>
        ))}
      </div>

      {detail.reason && (
        <div className="bg-slate-800/60 rounded-xl p-3 mb-5">
          <div className="text-[10px] text-slate-500 mb-1">Reason</div>
          <p className="text-xs text-slate-300">{detail.reason}</p>
        </div>
      )}

      {/* Approval history */}
      {detail.approvals?.length > 0 && (
        <div className="mb-5">
          <h4 className="text-[10px] text-slate-500 uppercase tracking-wide mb-2">Approval History</h4>
          <div className="space-y-2">
            {detail.approvals.map(ev => (
              <div key={ev.id} className="flex items-start gap-3 bg-slate-800/40 rounded-lg p-2.5">
                <div className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${ev.action === "APPROVED" ? "bg-emerald-500" : ev.action === "REJECTED" ? "bg-red-500" : "bg-amber-500"}`} />
                <div>
                  <div className="text-[10px] text-slate-300 font-medium">{ev.action} by {ev.actioned_by}</div>
                  <div className="text-[10px] text-slate-500">{new Date(ev.actioned_at).toLocaleString()}</div>
                  {ev.remarks && <div className="text-[10px] text-slate-400 mt-0.5">{ev.remarks}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {approvalError && <div data-testid="approval-error" className="mb-4 p-3 bg-red-900/40 border border-red-700 rounded-lg text-xs text-red-300">{approvalError}</div>}

      {isPending && (
        <div className="flex gap-2">
          <button data-testid="btn-approve-leave" onClick={onApprove} className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium py-2 rounded-lg transition-colors">
            Approve
          </button>
          <button data-testid="btn-reject-leave" onClick={onOpenReject} className="flex-1 bg-red-900 hover:bg-red-800 text-red-300 text-sm font-medium py-2 rounded-lg transition-colors border border-red-700">
            Reject
          </button>
        </div>
      )}

      {/* Reject modal */}
      {showRejectModal && (
        <div className="mt-4 bg-red-950/40 border border-red-800 rounded-xl p-4">
          <h4 className="text-xs font-medium text-red-300 mb-3">Rejection Reason (required)</h4>
          <textarea
            data-testid="reject-reason-input"
            rows={3} value={rejectReason} onChange={e => onRejectReasonChange(e.target.value)}
            placeholder="Enter mandatory rejection reason…"
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-200 focus:outline-none focus:border-red-500 resize-none mb-3"
          />
          <div className="flex gap-2">
            <button data-testid="btn-confirm-reject" onClick={onReject} className="flex-1 bg-red-700 hover:bg-red-600 text-white text-xs py-2 rounded-lg">
              Confirm Rejection
            </button>
            <button onClick={() => onSetShowRejectModal(false)} className="flex-1 bg-slate-800 text-slate-400 text-xs py-2 rounded-lg">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Attendance Panel ──────────────────────────────────────────────────────────
function AttendancePanel({ summary, calendar, schedule, loading, teamAttendance }) {
  if (loading) {
    return <div className="text-center py-10 text-xs text-slate-500">Loading attendance…</div>;
  }

  return (
    <div className="space-y-5">
      {/* Stats row */}
      {summary && (
        <div>
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Attendance Summary · {summary.sample_period}</h3>
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: "Present", val: summary.present_days, color: "text-emerald-400" },
              { label: "Absent", val: summary.absent_days, color: "text-red-400" },
              { label: "WFH", val: summary.wfh_days, color: "text-blue-400" },
              { label: "Exceptions", val: summary.exception_days, color: "text-amber-400" },
            ].map(s => (
              <div key={s.label} className="bg-slate-900 border border-slate-800 rounded-xl p-3 text-center">
                <div className={`text-2xl font-bold ${s.color}`}>{s.val}</div>
                <div className="text-[10px] text-slate-500 mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Work schedule */}
      {schedule && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
          <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Work Schedule</h4>
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: "Schedule", val: schedule.schedule_name },
              { label: "Type", val: schedule.schedule_type },
              { label: "Daily Hours", val: `${schedule.daily_hours || "—"}h` },
            ].map(r => (
              <div key={r.label}>
                <div className="text-[10px] text-slate-500">{r.label}</div>
                <div className="text-xs font-medium text-slate-300 mt-0.5">{r.val}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Calendar */}
      {calendar?.days?.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Recent Attendance</h3>
          <div className="overflow-hidden rounded-xl border border-slate-800">
            <table className="w-full text-xs" data-testid="attendance-calendar-table">
              <thead>
                <tr className="bg-slate-900/60">
                  <th className="px-4 py-2 text-left text-slate-500 font-medium">Date</th>
                  <th className="px-4 py-2 text-left text-slate-500 font-medium">Status</th>
                  <th className="px-4 py-2 text-right text-slate-500 font-medium">Worked</th>
                  <th className="px-4 py-2 text-right text-slate-500 font-medium">Exceptions</th>
                </tr>
              </thead>
              <tbody>
                {calendar.days.map((d, i) => (
                  <tr key={d.attendance_date} className={`border-t border-slate-800/60 ${i % 2 === 0 ? "bg-slate-900/20" : ""}`}>
                    <td className="px-4 py-2.5 text-slate-300">{new Date(d.attendance_date).toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" })}</td>
                    <td className="px-4 py-2.5">
                      <AttStatusBadge status={d.attendance_status_code} isHoliday={d.is_holiday} isWfh={d.is_wfh} />
                    </td>
                    <td className="px-4 py-2.5 text-right text-slate-400">{d.net_worked_minutes ? `${Math.round(d.net_worked_minutes / 60)}h ${d.net_worked_minutes % 60}m` : "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      {d.has_exception ? <span className="text-amber-400">⚠</span> : <span className="text-slate-700">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Team overview */}
      {teamAttendance.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Team Overview</h3>
          <div className="grid grid-cols-4 gap-2">
            {teamAttendance.slice(0, 8).map(e => (
              <div key={e.worker_id} className="bg-slate-900 border border-slate-800 rounded-xl p-3 text-center">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center text-xs font-bold text-slate-300 mx-auto mb-2">
                  {(e.display_name || "?")[0]}
                </div>
                <div className="text-[10px] text-slate-300 truncate">{e.display_name?.split(" ")[0]}</div>
                <div className="mt-1">
                  <span className="text-emerald-400 font-bold text-sm">{e.present_days}</span>
                  <span className="text-slate-600 text-[10px]">/{e.present_days + e.absent_days}d</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
